import { useCallback } from 'react';
import { keyIsEscape } from "../../shared/utils/keyPressEvent/keyPressEvent";
import { useDisableScroll } from '../useDisableScroll/useDisableScroll';
import { useFocusTrap } from '../useFocusTrap/useFocusTrap';
import { useEventListener } from '../useEventListener/useEventListener';
/**
 * A react hook that ensures popout components are accessible
 *
 * @param popOutRef a ref to the popout element
 * @param isOpen if true the popout is open
 * @param handleClose a callback to be executed when the popout is closed
 * @param optionOverrides options that can override the default behavior of the hook
 * @param optionOverrides.disableScroll if true disables scrolling on the page
 * @param optionOverrides.focusLock if true enables a focus trap on the specified element
 * @param optionOverrides.escapeToClose if true esc key will execute a handleClose callback
 * @param optionOverrides.focusableElementQueryArray an array of selectors to be queried which are allowed to be focused on
 * @param optionOverrides.focusedElementIndex the index of the element that should initially have focus
 *
 * @returns a function that resets the focus trap. This can be useful if the focusable elements
 * inside the popout changes, but the focusableElementQueryArray stays the same
 */
export const useAccessiblePopout = (popOutRef, isOpen, handleClose, optionOverrides) => {
    const options = {
        disableScroll: true,
        focusLock: true,
        escapeToClose: true,
        focusableElementQueryArray: ['a', 'button', 'textarea', 'input', 'select', 'iframe'],
        focusedElementIndex: 0,
        ...optionOverrides,
    };
    const focusElements = isOpen && options.focusLock && options.focusableElementQueryArray ? options.focusableElementQueryArray : [];
    const handleEscapeKeyPress = useCallback((event) => {
        if (keyIsEscape(event)) {
            handleClose();
        }
    }, [handleClose]);
    useDisableScroll(isOpen && options.disableScroll);
    useEventListener('keydown', handleEscapeKeyPress, popOutRef, isOpen && options.escapeToClose);
    const refreshFocusableElements = useFocusTrap(popOutRef, focusElements, options.focusedElementIndex);
    return refreshFocusableElements;
};
